import { useEffect, useState, type CSSProperties } from 'react'
import {
  LANDING_EMBED_VIEWPORT_EVENT,
  type IframeVisibleSlice,
} from '@/lib/iframeVisibleSlice'

function isLandingEmbedIframe(): boolean {
  try {
    return typeof window !== 'undefined' && window.parent != null && window.parent !== window
  } catch {
    return true
  }
}

function isViewportSlice(value: unknown): value is IframeVisibleSlice {
  if (!value || typeof value !== 'object') {
    return false
  }
  const record = value as Record<string, unknown>
  return Number.isFinite(record.visibleTop) && Number.isFinite(record.visibleHeight)
}

export function useLandingEmbedViewportPin(enabled: boolean): CSSProperties | undefined {
  const [slice, setSlice] = useState<IframeVisibleSlice | null>(null)

  useEffect(() => {
    if (!enabled || !isLandingEmbedIframe()) {
      setSlice(null)
      return
    }

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) {
        return
      }
      const data = event.data
      if (!data || typeof data !== 'object') {
        return
      }
      const record = data as Record<string, unknown>
      if (record.type !== LANDING_EMBED_VIEWPORT_EVENT || record.action) {
        return
      }
      const visibleTop = Number(record.visibleTop)
      const visibleHeight = Number(record.visibleHeight)
      if (!Number.isFinite(visibleTop) || !Number.isFinite(visibleHeight) || visibleHeight < 1) {
        return
      }
      setSlice({
        visibleTop: Math.max(0, visibleTop),
        visibleHeight: Math.max(0, visibleHeight),
      })
    }

    window.addEventListener('message', onMessage)
    window.parent.postMessage({ type: LANDING_EMBED_VIEWPORT_EVENT, action: 'subscribe' }, '*')

    return () => {
      window.removeEventListener('message', onMessage)
      try {
        window.parent.postMessage({ type: LANDING_EMBED_VIEWPORT_EVENT, action: 'unsubscribe' }, '*')
      } catch {
        // iframe pode ter sido desmontado
      }
    }
  }, [enabled])

  if (!slice || !isViewportSlice(slice) || slice.visibleHeight < 1) {
    return undefined
  }

  return {
    top: slice.visibleTop,
    height: slice.visibleHeight,
    bottom: 'auto',
  }
}
